import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { persist } from '../../../../qwanyx-core/api-client';
import { newQwanta } from '../../../../qwanyx-core/newQwanta';

// Cart Actions
const CART_ACTIONS = {
    ADD_ITEM: 'ADD_ITEM',
    REMOVE_ITEM: 'REMOVE_ITEM',
    UPDATE_QUANTITY: 'UPDATE_QUANTITY',
    CLEAR_CART: 'CLEAR_CART',
    UPDATE_CUSTOMER_INFO: 'UPDATE_CUSTOMER_INFO',
    SET_CART_DATA: 'SET_CART_DATA'
};

// Initial cart state
const initialCartState = {
    items: [], // Array of {productId, quantity, productData}
    customerInfo: {
        email: '',
        phone: '',
        address: ''
    },
    totalItems: 0,
    orderDomain: 'autodinStockOrder' // Default domain for orders
};

// Cart reducer
const cartReducer = (state, action) => {
    switch (action.type) {
        case CART_ACTIONS.ADD_ITEM: {
            const { productId, productData, quantity = 1 } = action.payload;
            const existingItemIndex = state.items.findIndex(item => item.productId === productId);
            
            let newItems;
            if (existingItemIndex >= 0) {
                // Update existing item quantity
                newItems = [...state.items];
                newItems[existingItemIndex] = {
                    ...newItems[existingItemIndex],
                    quantity: newItems[existingItemIndex].quantity + quantity
                };
            } else {
                // Add new item
                newItems = [...state.items, { productId, productData, quantity }];
            }
            
            const totalItems = newItems.reduce((sum, item) => sum + item.quantity, 0);
            
            return {
                ...state,
                items: newItems,
                totalItems
            };
        }
        
        case CART_ACTIONS.REMOVE_ITEM: {
            const { productId } = action.payload;
            const newItems = state.items.filter(item => item.productId !== productId);
            const totalItems = newItems.reduce((sum, item) => sum + item.quantity, 0);
            
            return {
                ...state,
                items: newItems,
                totalItems
            };
        }
        
        case CART_ACTIONS.UPDATE_QUANTITY: {
            const { productId, quantity } = action.payload;
            
            if (quantity <= 0) {
                // Remove item if quantity is 0 or negative
                return cartReducer(state, { type: CART_ACTIONS.REMOVE_ITEM, payload: { productId } });
            }
            
            const newItems = state.items.map(item =>
                item.productId === productId ? { ...item, quantity } : item
            );
            
            const totalItems = newItems.reduce((sum, item) => sum + item.quantity, 0);
            
            return {
                ...state,
                items: newItems,
                totalItems
            };
        }
        
        case CART_ACTIONS.CLEAR_CART: {
            return {
                ...state,
                items: [],
                totalItems: 0
            };
        }
        
        case CART_ACTIONS.UPDATE_CUSTOMER_INFO: {
            return {
                ...state,
                customerInfo: {
                    ...state.customerInfo,
                    ...action.payload
                }
            };
        }
        
        case CART_ACTIONS.SET_CART_DATA: {
            return {
                ...state,
                ...action.payload
            };
        }
        
        default:
            return state;
    }
};

// Create contexts
export const CartContext = createContext();
const CartDispatchContext = createContext();

// Cart Provider component
export const CartProvider = ({ children, orderDomain = 'autodinStockOrder' }) => {
    const [cartState, dispatch] = useReducer(cartReducer, {
        ...initialCartState,
        orderDomain
    });
    
    // Load cart from localStorage on mount
    useEffect(() => {
        const savedCart = localStorage.getItem(`qwanyx-cart-${orderDomain}`);
        if (savedCart) {
            try {
                const cartData = JSON.parse(savedCart);
                dispatch({ type: CART_ACTIONS.SET_CART_DATA, payload: cartData });
            } catch (error) {
                console.error('[CartProvider] Error loading cart from localStorage:', error);
            }
        }
    }, [orderDomain]);
    
    // Save cart to localStorage whenever it changes
    useEffect(() => {
        try {
            localStorage.setItem(`qwanyx-cart-${orderDomain}`, JSON.stringify(cartState));
        } catch (error) {
            console.error('[CartProvider] Error saving cart to localStorage:', error);
        }
    }, [cartState, orderDomain]);
    
    return (
        <CartContext.Provider value={cartState}>
            <CartDispatchContext.Provider value={dispatch}>
                {children}
            </CartDispatchContext.Provider>
        </CartContext.Provider>
    );
};

// Custom hooks for using cart
export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};

export const useCartDispatch = () => {
    const context = useContext(CartDispatchContext);
    if (!context) {
        throw new Error('useCartDispatch must be used within a CartProvider');
    }
    return context;
};

// Safe versions of hooks that return null instead of throwing
export const useSafeCart = () => {
    const context = useContext(CartContext);
    return context || null;
};

export const useSafeCartDispatch = () => {
    const context = useContext(CartDispatchContext);
    return context || null;
};

// Cart action creators
export const cartActions = {
    addItem: (productId, productData, quantity = 1) => ({
        type: CART_ACTIONS.ADD_ITEM,
        payload: { productId, productData, quantity }
    }),
    
    removeItem: (productId) => ({
        type: CART_ACTIONS.REMOVE_ITEM,
        payload: { productId }
    }),
    
    updateQuantity: (productId, quantity) => ({
        type: CART_ACTIONS.UPDATE_QUANTITY,
        payload: { productId, quantity }
    }),
    
    clearCart: () => ({
        type: CART_ACTIONS.CLEAR_CART
    }),
    
    updateCustomerInfo: (customerInfo) => ({
        type: CART_ACTIONS.UPDATE_CUSTOMER_INFO,
        payload: customerInfo
    })
};

// Helper function to create order from cart
export const createOrderFromCart = async (cartState) => {
    // Create a new qwanta for the order
    const newOrder = newQwanta(
        null, // editorId
        cartState.orderDomain, // frameId (di)
        null, // editorParent
        'order', // qwantaType
        {
            // Form data - order details
            customerEmail: cartState.customerInfo.email,
            customerPhone: cartState.customerInfo.phone,
            customerAddress: cartState.customerInfo.address,
            selectedProducts: cartState.items.map(item => item.productId),
            productDetails: cartState.items.map(item => ({
                productId: item.productId,
                quantity: item.quantity,
                productData: item.productData
            })),
            orderDate: new Date().toISOString(),
            totalItems: cartState.totalItems,
            orderStatus: 'pending'
        }
    );
    
    try {
        // Use persist to save the new order
        const result = await persist(newOrder, newOrder._id, newOrder.di, newOrder.ed);
        console.log('[CartContext] Order created successfully:', result);
        return result;
    } catch (error) {
        console.error('[CartContext] Error creating order:', error);
        throw error;
    }
};

export default CartProvider;